import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  CheckSquare, 
  MessageSquare, 
  Calendar, 
  CalendarCheck,
  CalendarClock,
  DollarSign, 
  CreditCard,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  Target,
  Upload,
  UserPlus,
  Tag,
  MapPin,
  Zap,
  BarChart3,
  FileText,
  UserCheck,
  UserCircle,
  Package,
  Brain,
  Calculator,
  Globe
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { usePermissions } from '../../context/PermissionsContext';
import { logger } from '../../utils/logger';
import ReminderSystem from '../ReminderSystem';
import { clearStoredAuthToken } from '../../utils/authFetchPatch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map nav item labels to notification count keys
const NOTIFICATION_MAP = {
  'Leads': 'leads',
  'Events': 'events',
  'Appointments': 'appointments',
  'Tasks': 'tasks',
  'Calendar': 'calendar',
  'Customers': 'customers',
  'Contracts': 'contracts',
  'Installments': 'installments',
  'Payments': 'payments',
  'User Requests': 'user_requests'
};

const navItems = [
  { icon: Brain, label: 'Intelligence', path: '/intelligence', moduleId: 'intelligence', roles: ['owner', 'admin', 'sales_manager', 'finance', 'top_management'] },
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', moduleId: 'dashboard', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: BarChart3, label: 'Funnel Analytics', path: '/funnel', moduleId: 'funnel_analytics', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: Users, label: 'Leads', path: '/leads', moduleId: 'leads', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: Upload, label: 'Import Leads', path: '/leads/import', moduleId: 'import_leads', roles: ['owner', 'admin', 'sales_manager'] },
  { icon: MapPin, label: 'Field Data', path: '/leads/field-data', moduleId: 'field_data', roles: ['owner', 'admin', 'sales_manager', 'account_executive'] },
  { icon: Tag, label: 'Tags', path: '/tags', moduleId: 'tags', roles: ['owner', 'admin', 'sales_manager'] },
  { icon: Package, label: 'Products', path: '/products', moduleId: 'products', roles: ['owner', 'admin', 'sales_manager', 'finance', 'top_management'] },
  { icon: CalendarCheck, label: 'Events', path: '/events', moduleId: 'events', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: CalendarClock, label: 'Appointments', path: '/appointments', moduleId: 'appointments', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: CheckSquare, label: 'Tasks', path: '/tasks', moduleId: 'tasks', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: MessageSquare, label: 'Communications', path: '/communications', moduleId: 'communications', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'top_management'] },
  { icon: Calendar, label: 'Calendar', path: '/calendar', moduleId: 'calendar', roles: ['owner', 'admin', 'sales_manager', 'account_executive', 'finance', 'top_management'] },
  { icon: UserCheck, label: 'Customers', path: '/finance/customers', moduleId: 'customers', roles: ['owner', 'admin', 'finance', 'sales_manager', 'top_management'] },
  { icon: FileText, label: 'Contracts', path: '/finance/contracts', moduleId: 'contracts', roles: ['owner', 'admin', 'finance', 'sales_manager', 'top_management'] },
  { icon: FileText, label: 'Booking Forms', path: '/booking-forms', moduleId: 'booking_forms', roles: ['owner', 'admin', 'finance', 'sales_manager'] },
  { icon: DollarSign, label: 'Installments', path: '/installments', moduleId: 'installments', roles: ['owner', 'admin', 'finance', 'sales_manager', 'account_executive', 'top_management'] },
  { icon: CreditCard, label: 'Payments', path: '/payments', moduleId: 'payments', roles: ['owner', 'admin', 'finance', 'sales_manager', 'top_management'] },
  { icon: Zap, label: 'Automation', path: '/automation-settings', moduleId: 'automation', roles: ['owner', 'admin', 'sales_manager', 'account_executive'] },
  { icon: Globe, label: 'Landing Pages', path: '/landing-pages', moduleId: 'landing_pages', roles: ['owner', 'admin'] },
  { icon: UserPlus, label: 'User Requests', path: '/users/requests', moduleId: 'user_requests', roles: ['owner', 'admin'] },
  { icon: Calculator, label: 'Accounting', path: '/accounting', moduleId: 'accounting', roles: ['owner', 'admin'] },
  { icon: Settings, label: 'Users', path: '/users', moduleId: 'users', roles: ['owner', 'admin', 'top_management'] },
];

export default function AppLayout({ children, user }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { isModuleEnabled, modules, moduleAction } = usePermissions();

  // Fetch notification counts
  const fetchNotificationCounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/counts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotificationCounts(data.counts || {});
      }
    } catch (error) {
      logger.error('Error fetching notification counts:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotificationCounts();
    // Refresh counts every 2 minutes
    const interval = setInterval(fetchNotificationCounts, 120000);
    return () => clearInterval(interval);
  }, [fetchNotificationCounts]);

  // Refresh counts when navigating
  useEffect(() => {
    fetchNotificationCounts();
  }, [location.pathname, fetchNotificationCounts]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      clearStoredAuthToken();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      logger.error('Logout error:', error);
      clearStoredAuthToken();
      navigate('/login');
    }
  };

  const filteredNavItems = navItems.filter(item => {
    // Owner always sees every nav item
    if (user?.role === 'owner') return true;

    // If the module has been EXPLICITLY configured in the user's custom
    // permission set AND it's enabled + view-allowed, show it regardless of
    // whether the user's role is in the hardcoded default allowlist. This is
    // what lets, e.g., an Account Executive who has been granted Payments +
    // Contracts access see those nav items.
    const hasCustomModule = item.moduleId && modules && Object.prototype.hasOwnProperty.call(modules, item.moduleId);
    if (hasCustomModule) {
      return moduleAction(item.moduleId, 'view') === true;
    }

    // No custom override — fall back to hardcoded default roles array, then
    // the module-enabled check (for modules that can be globally toggled).
    if (!item.roles.includes(user?.role || 'account_executive')) return false;
    if (item.moduleId && !isModuleEnabled(item.moduleId)) return false;
    return true;
  });

  const getNotificationCount = (label) => {
    const key = NOTIFICATION_MAP[label];
    if (!key) return 0;
    return notificationCounts[key] || 0;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'Admin',
      sales_manager: 'Sales Manager',
      account_executive: 'Account Executive',
      finance: 'Finance',
      top_management: 'Top Management'
    };
    return badges[role] || role;
  };

  // Notification Badge Component
  const NotificationBadge = ({ count }) => {
    if (!count || count <= 0) return null;
    return (
      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 shadow-sm">
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex flex-col bg-slate-900 transition-all duration-300 ease-out ${
          collapsed ? 'w-16' : 'w-56'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-between p-3 border-b border-slate-700/50">
          {!collapsed ? (
            <div className="flex items-center gap-2">
              <img 
                src="/logo-white.png" 
                alt="Goldsands" 
                className="h-9 w-9 object-contain flex-shrink-0"
              />
              <div className="flex flex-col justify-center">
                <h1 className="font-heading text-sm text-white font-semibold tracking-wide leading-tight">
                  GOLDSANDS
                </h1>
                <span className="text-[9px] text-slate-400 font-medium tracking-widest">GROUP INC</span>
              </div>
            </div>
          ) : (
            <img 
              src="/logo-white.png" 
              alt="Goldsands" 
              className="h-9 w-9 object-contain mx-auto"
            />
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200 ${collapsed ? 'mx-auto mt-2' : ''}`}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const count = getNotificationCount(item.label);
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full sidebar-item ${isActive ? 'active' : ''} relative`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative">
                  <Icon size={16} strokeWidth={1.5} />
                  {collapsed && <NotificationBadge count={count} />}
                </div>
                {!collapsed && (
                  <>
                    <span className="font-body text-xs flex-1 text-left">{item.label}</span>
                    {count > 0 && (
                      <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-slate-700">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className={`w-full flex items-center gap-2 p-1.5 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors ${collapsed ? 'justify-center' : ''}`}
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-7 w-7 border border-gold/30">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-gold/20 text-gold text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <p className="text-xs font-medium truncate">{user?.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{getRoleBadge(user?.role)}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/profile')}
                data-testid="my-profile-btn"
              >
                <UserCircle className="mr-2 h-4 w-4" />
                <span>My Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-700/50">
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <img 
              src="/logo-white.png" 
              alt="Goldsands" 
              className="h-7 w-7 object-contain flex-shrink-0"
            />
            <h1 className="font-heading text-base text-white font-semibold truncate">GOLDSANDS</h1>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-slate-400 hover:text-white rounded-xl transition-colors relative"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
            {/* Show total notification count on mobile menu button */}
            {Object.values(notificationCounts).reduce((a, b) => a + b, 0) > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-1">
                {Object.values(notificationCounts).reduce((a, b) => a + b, 0) > 99 ? '99+' : Object.values(notificationCounts).reduce((a, b) => a + b, 0)}
              </span>
            )}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop overlay to close drawer on outside tap */}
            <div
              className="fixed inset-0 top-14 bg-black/40 backdrop-blur-sm z-40"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <nav className="relative z-50 p-4 space-y-2 bg-slate-900 border-t border-slate-700 max-h-[85vh] overflow-y-auto shadow-2xl">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const count = getNotificationCount(item.label);
              
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full sidebar-item ${isActive ? 'active' : ''} min-h-[44px]`}
                >
                  <Icon size={20} strokeWidth={1.5} />
                  <span className="font-body flex-1 text-left">{item.label}</span>
                  {count > 0 && (
                    <span className="min-w-[20px] h-[20px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
            <div className="pt-2 mt-2 border-t border-slate-700">
              <div className="px-3 py-2 text-xs text-slate-400">
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="truncate">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full sidebar-item text-red-400 hover:text-red-300 min-h-[44px]"
              >
                <LogOut size={20} strokeWidth={1.5} />
                <span className="font-body">Logout</span>
              </button>
            </div>
            </nav>
          </>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto md:pt-0 pt-14">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* AE Reminder System (polling + popup + beep) */}
      <ReminderSystem user={user} />
    </div>
  );
}
